import React, { useState, useEffect, useCallback } from "react";
import { Modal, List, Typography, Empty, Tag, Button, Space, notification as antdNotification, Pagination } from "antd";
import { getRequest, postRequest } from "../service/index";
import { formatDateTime } from "../utils/dateTime";
import { Info, Megaphone, Calendar, Clock, Shuffle, Wallet, CalendarCheck, MonitorX   } from "lucide-react";

const NotificationModal = ({ open, onClose, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(4);

  const typeMetaMap = {
    general: { label: 'General', color: '#1677ff', Icon: Info },
    announcement: { label: 'Announcement', color: '#722ed1', Icon: Megaphone },
    LeaveRequest: { label: 'Leave Request', color: '#13c2c2', Icon: Calendar },
    OverTimeRequest: { label: 'Overtime Request', color: '#fa8c16', Icon: Clock },
    RequestShift: { label: 'Shift Request', color: '#2f54eb', Icon: Shuffle },
    payroll: { label: 'Payroll', color: '#52c41a', Icon: Wallet },
    Offboarding: { label: 'Off Boarding', color: '#dc3545', Icon: MonitorX   },
    PublicHoliday: { label: 'Public Holiday', color: '#198754', Icon: CalendarCheck },
  };

  const getTypeMeta = (type) => typeMetaMap[type] || typeMetaMap.general;

  const getPriorityStyle = (priority) => {
    const p = (priority || '').toLowerCase();
    switch (p) {
      case 'low':
        return { bg: 'bg-yellow-50', border: 'border-yellow-300', tag: 'gold' };
      case 'medium':
        return { bg: 'bg-green-50', border: 'border-green-300', tag: 'green' };
      case 'high':
        return { bg: 'bg-red-50', border: 'border-red-300', tag: 'red' };
      default:
        return { bg: 'bg-gray-50', border: 'border-gray-200', tag: 'default' };
    }
  };

  // Reset to first page when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
    }
  }, [open]);

  // Fetch initial notifications from API
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getRequest(`/api/notifications?unreadOnly=true`);
      if (data?.notifications && Array.isArray(data.notifications)) {
        setNotifications(data.notifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!open) return;
    
    fetchNotifications();
  }, [open]);

  // Merge live notifications from SSE events broadcasted by navbar
  useEffect(() => {
    const handler = (e) => {
      const items = e.detail || [];
      if (!items || items.length === 0) return;
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n._id || n.id));
        const merged = [...items.filter(n => !existingIds.has(n._id || n.id)), ...prev];
        return merged;
      });
      const first = items[0];
      if (first?.title || first?.body) {
        antdNotification.info({
          message: first.title || 'New notification',
          description: first.body || '',
          placement: 'topRight',
          duration: 3,
        });
      }
    };
    window.addEventListener('notifications:new', handler);
    return () => window.removeEventListener('notifications:new', handler);
  }, []);

  // Mark notifications as read when modal opens
  useEffect(() => {
    // no-op; explicit mark with handlers below
  }, [open, notifications]);

  const markOneRead = useCallback(async (notificationId) => {
    try {
      await postRequest(`/api/notifications/read`, { notificationId });
      setNotifications(prev => prev.map(n => {
        const id = n._id || n.id;
        if (id === notificationId) return { ...n, read: true };
        return n;
      }));
      fetchNotifications();
      window.dispatchEvent(new Event('notifications:read-all'));
    } catch (e) {
      
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await postRequest(`/api/notifications/read-all`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      window.dispatchEvent(new Event('notifications:read-all'));
    } catch (e) {
      // ignore
    }
  }, []);

  // Calculate pagination
  const totalNotifications = notifications.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentNotifications = notifications.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Modal
      title="Notifications"
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={600}
    >
      <div className="flex justify-end mb-3">
        <Space>
          <Button onClick={markAllRead} size="small">Mark all as read</Button>
        </Space>
      </div>
      {notifications.length > 0 ? (
        <>
          <List
            loading={loading}
            dataSource={currentNotifications}
            renderItem={(item) => {
              const id = item._id || item.id;
              const isRead = item.read || (Array.isArray(item.readBy) && item.readBy.length > 0);
              
              // Skip notifications without sender data
              if (!item.senderId) {
                return null;
              }
              
              const senderName = item?.senderId?.companyName || 
                (item?.senderId?.firstName && item?.senderId?.lastName ? 
                  `${item.senderId.firstName} ${item.senderId.lastName}` : 
                  null);
              
              // Skip notifications without valid sender name
              if (!senderName) {
                return null;
              }
              
              const { label: typeLabel, color: typeColor, Icon: TypeIcon } = getTypeMeta(item.type);
              const { bg: priorityBg, border: priorityBorder, tag: priorityTagColor } = getPriorityStyle(item.priority);
              return (
                <List.Item
                  key={id}
                  actions={[
                    <Button key="read" type="link" size="small" onClick={() => markOneRead(id)}>
                      {isRead ? 'Mark unread' : 'Mark read'}
                    </Button>
                  ]}
                  className={`p-3 border rounded-lg mb-3 ${priorityBg} ${priorityBorder}`}
                >
                  <div className="w-full flex gap-2">
                    <div 
                      className="h-8 w-8 rounded-full text-white flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: typeColor }}
                    >
                      <TypeIcon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1">
                        <Typography.Text strong className="text-xs text-gray-900 flex-1 pr-2">
                          {item.title}
                        </Typography.Text>
                        <div className="flex-shrink-0">
                          <Tag 
                            className="border text-xs"
                            style={{ borderColor: typeColor, color: typeColor }}
                          >
                            {typeLabel}
                          </Tag>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-3">
                        <Typography.Text type="secondary" className="text-xs">
                        {senderName} at {item.createdAt ? formatDateTime(item.createdAt) : ''}
                        </Typography.Text>
                        {/* <Typography.Text type="secondary" className="text-xs">
                          {senderName}
                        </Typography.Text> */}
                      </div>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
          {totalNotifications > pageSize && (
            <div className="flex justify-center mt-4">
              <Pagination
                current={currentPage}
                total={totalNotifications}
                pageSize={pageSize}
                onChange={handlePageChange}
                showSizeChanger={false}
                showQuickJumper={false}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} notifications`}
                size="small"
              />
            </div>
          )}
        </>
      ) : (
        <Empty description="No notifications" />
      )}
    </Modal>
  );
};

export default NotificationModal;